/**
 * WhyNutramiloPage.jsx — iter671
 *
 * Public landing page "Защо Nutramilo" — сравнителна страница vs multi-omics
 * платформи без споменаване на конкуренти. Conversion-oriented.
 *
 * Route: /why-nutramilo (public, no auth)
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Activity, Brain, FlaskConical, HeartPulse, Sparkles,
  Stethoscope, Users, Lock, Zap, ChevronDown, Play, ShieldCheck,
} from "lucide-react";

const TWINS = [
  { emoji: "🧬", label: "Хормонален" },
  { emoji: "🍽️", label: "Метаболитен" },
  { emoji: "❤️", label: "Сърдечно-съдов" },
  { emoji: "🧠", label: "Когнитивен" },
  { emoji: "🩸", label: "Лабораторен" },
  { emoji: "🦠", label: "Чревен" },
  { emoji: "🧹", label: "Детокс" },
  { emoji: "😴", label: "Съня" },
  { emoji: "🧘", label: "Стрес" },
  { emoji: "🛡️", label: "Имунен" },
  { emoji: "🦴", label: "Костен" },
  { emoji: "🧬", label: "Наследствен" },
  { emoji: "🦷", label: "Дентален" },
  { emoji: "🎙️", label: "Гласов биомаркер" },
  { emoji: "⏳", label: "Биологична възраст" },
  { emoji: "📊", label: "Метаболитен Phenotype" },
  { emoji: "🔄", label: "Cross-twin физиология" },
];
const BHI_TWIN = {
  emoji: "🌸",
  label: "BHI · Гръдно здраве",
  desc: "Най-задълбоченият специализиран близнак — фузира DUTCH/ДНК/серумни хормони с метаболитната среда; влияе на 5 от другите близнаци (Хормонален, Метаболитен, Детокс, Стрес, Имунен) в реално време.",
};

const ROWS = [
  { ours: "20 свързани дигитални близнака (включително гръдно здраве)", theirs: "7 области в един обобщен индекс", icon: Brain },
  { ours: "Real-time AI генерира уникално съдържание за Вас", theirs: "Статичен PDF доклад с template препоръки", icon: Sparkles },
  { ours: "Качете каквото имате — DUTCH, ДНК, лаборатории, мамограма + OCR", theirs: "Изисква собствен test-kit", icon: FlaskConical },
  { ours: "Coach AI + седмични streaks + nudges (връщате се ежеседмично)", theirs: "Прочитате доклада веднъж и забравяте", icon: HeartPulse },
  { ours: "Plate Simulator — снимка на ястие → AI прогноза за тялото", theirs: "Общи хранителни насоки", icon: Activity },
  { ours: "Voice biomarkers — стрес от 30-сек глас", theirs: "—", icon: Zap },
  { ours: "Двустранна платформа — пациент + лекарски панел", theirs: "Само пациент", icon: Stethoscope },
  { ours: "Cross-twin reasoning — нисък E2 автоматично влияе на Cognitive + Metabolic + BHI", theirs: "Силоси (всеки маркер в своя секция)", icon: Users },
  { ours: "Transparent Fusion Audit — всеки скор показва източника си", theirs: "Black-box index", icon: ShieldCheck },
  { ours: "BG-first patient-friendly език — никакво „предсказваме рак“", theirs: "Често чуждици, технически тон", icon: Lock },
];

function GradientText({ children }) {
  return (
    <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 bg-clip-text text-transparent">
      {children}
    </span>
  );
}

export default function WhyNutramiloPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-purple-50/30 text-slate-900" data-testid="why-nutramilo-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-20 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100 text-pink-700 text-xs font-bold mb-5">
          <Sparkles className="w-3.5 h-3.5" /> Защо Nutramilo
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5">
          Не пореден <span className="opacity-50">multi-omics доклад.</span><br/>
          <GradientText>Жив AI помощник за тялото Ви.</GradientText>
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed mb-7">
          Nutramilo вижда тялото Ви като <strong>17 свързани системи + 1 специалист за гръдното здраве (BHI)</strong> — не като списък от тестове.
          Качвате данните, които вече имате. AI ги свързва. Coach Ви води ежеседмично. Лекарят Ви получава ясни данни.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold hover:opacity-95 hover:scale-[1.02] transition-all shadow-lg"
            data-testid="why-cta-start"
          >
            Започнете безплатно <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-white border border-slate-200 font-bold hover:bg-slate-50"
            data-testid="why-cta-tour"
          >
            <Play className="w-4 h-4" /> Кратък тур (2 мин)
          </a>
        </div>
      </section>

      {/* Video placeholder */}
      <section id="how-it-works" className="max-w-5xl mx-auto px-5 pb-16">
        <div
          className="relative aspect-video rounded-3xl overflow-hidden border border-pink-200 shadow-2xl bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 flex items-center justify-center group cursor-pointer"
          data-testid="why-video-placeholder"
        >
          {/* Replace src with actual YouTube/Vimeo embed when video is ready */}
          <div className="text-center px-6">
            <div className="w-20 h-20 rounded-full bg-white shadow-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Play className="w-8 h-8 text-pink-600 ml-1" fill="currentColor" />
            </div>
            <p className="text-lg font-bold text-slate-800">Кратко въведение в Nutramilo</p>
            <p className="text-sm text-slate-600 mt-1">2 минути · BG audio · Subtitles</p>
          </div>
          {/* When video is ready, replace the placeholder div with:
              <iframe className="absolute inset-0 w-full h-full" src="https://www.youtube.com/embed/VIDEO_ID" allow="autoplay; encrypted-media" allowFullScreen />
          */}
        </div>
      </section>

      {/* 18 Twins visual (BHI = 18-ти) */}
      <section className="max-w-6xl mx-auto px-5 pb-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-2">
          20 дигитални близнака — една обща мрежа
        </h2>
        <p className="text-center text-sm text-slate-600 mb-8 max-w-2xl mx-auto">
          20 дигитални близнака покриват цялото тяло — от обмяната до гръдното здраве, като са свързани с над 110 физиологични зависимости. <strong>Здраве на гърдата</strong> е най-дълбоко специализираният: той обединява данни от 5 от останалите близнаци.
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="why-twins-grid">
          {TWINS.map((t, i) => (
            <div
              key={i}
              className="aspect-square rounded-2xl bg-white border border-slate-200 hover:border-pink-300 hover:shadow-lg transition-all flex flex-col items-center justify-center p-3 text-center"
            >
              <span className="text-2xl mb-1">{t.emoji}</span>
              <span className="text-[11px] font-bold leading-tight">{t.label}</span>
            </div>
          ))}
        </div>

        {/* BHI as the 18th — special highlighted card with cross-twin connections */}
        <div
          className="rounded-3xl bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 border-2 border-pink-300 p-5 sm:p-6 shadow-xl"
          data-testid="why-twin-bhi-special"
        >
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-3xl sm:text-4xl shrink-0 shadow-lg">
              {BHI_TWIN.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-pink-200 text-pink-800 text-[10px] font-bold uppercase tracking-wide mb-1">
                <Sparkles className="w-3 h-3" /> Специализиран близнак
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold mb-1.5">{BHI_TWIN.label}</h3>
              <p className="text-sm text-slate-700 leading-relaxed mb-3">{BHI_TWIN.desc}</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="text-[11px] font-bold text-slate-500">Свързан с:</span>
                {["Хормонален", "Метаболитен", "Детокс", "Стрес", "Имунен"].map((n) => (
                  <span key={n} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-pink-200 text-pink-700 font-semibold">
                    {n} ↔
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-5xl mx-auto px-5 pb-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-2">
          По какво се различаваме
        </h2>
        <p className="text-center text-sm text-slate-600 mb-8">
          Спрямо типична multi-omics платформа
        </p>
        <div className="grid md:grid-cols-2 gap-3" data-testid="why-comparison-grid">
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-5 border-2 border-pink-300">
            <div className="text-xs font-bold uppercase tracking-wide text-pink-700 mb-2">Nutramilo</div>
            <ul className="space-y-3">
              {ROWS.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-pink-500 font-bold shrink-0">✓</span>
                  <span>{r.ours}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Типична multi-omics платформа</div>
            <ul className="space-y-3">
              {ROWS.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-slate-400 shrink-0">·</span>
                  <span>{r.theirs}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What makes us unique */}
      <section className="max-w-6xl mx-auto px-5 pb-16">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-8">
          Какво <GradientText>само Nutramilo</GradientText> прави
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: "📸", title: "Plate Simulator", desc: "Снимка на ястие → AI прогноза за 5 sub-environment-а в реално време." },
            { icon: "🎙️", title: "Voice Biomarkers", desc: "30-секунден глас → анализ на хормонален стрес и невро-състояние." },
            { icon: "🌸", title: "Breast Health Intelligence", desc: "Най-задълбочения специализиран модул за гръдно здраве с transparent Fusion Audit." },
            { icon: "🤝", title: "Coach AI + Streaks", desc: "Седмични nudges, месечни доклади, поведенческа промяна — не еднократен PDF." },
            { icon: "🩺", title: "Лекарски панел", desc: "Двустранна платформа — лекарят вижда пациентския профил и получава нотификации." },
            { icon: "🔬", title: "Open Lab Ingest", desc: "Качете каквато имате лаборатория — OCR fallback за скан PDF-и, ДНК, DUTCH, серумни хормони." },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl bg-white p-5 border border-slate-200 hover:border-pink-300 hover:shadow-lg transition-all" data-testid={`why-unique-${i}`}>
              <div className="text-3xl mb-2">{c.icon}</div>
              <h3 className="text-base font-extrabold mb-1">{c.title}</h3>
              <p className="text-sm text-slate-600 leading-snug">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-5 pb-24 text-center">
        <div className="rounded-3xl bg-gradient-to-br from-pink-500 via-rose-500 to-purple-600 p-10 text-white shadow-2xl">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">
            Готови ли сте да видите тялото си като система?
          </h2>
          <p className="text-pink-50 mb-6 leading-relaxed">
            Започнете безплатно. Качете първото си изследване. AI ще се погрижи за останалото.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl bg-white text-pink-600 font-bold hover:scale-[1.03] transition-all shadow-xl"
            data-testid="why-cta-bottom"
          >
            Започнете безплатно <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-pink-100 mt-4 italic">
            Образователен помощник — не заменя медицинска консултация.
          </p>
        </div>
      </section>
    </div>
  );
}
